import { useUser } from "@dashboard/auth";
import {
  PunchKindEnum,
  usePortalHomeQuery,
  usePortalPeriodQuery,
  usePortalPunchCreateMutation,
} from "@dashboard/graphql";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { DayCard, ErrorText, KIND_MESSAGES, Page, Section } from "../components";
import { DailySign } from "../DailySign";
import { getDeviceId, getKioskToken } from "../device";
import { portalMessages as m } from "../messages";
import { portalPaths } from "../urls";
import { formatMinutes, formatTime, lisbonToIso, today } from "../utils";

const STATUS = {
  [PunchKindEnum.IN]: m.statusIn,
  [PunchKindEnum.BREAK_END]: m.statusIn,
  [PunchKindEnum.BREAK_START]: m.statusBreak,
  [PunchKindEnum.OUT]: m.statusOut,
};

export const Today = () => {
  const intl = useIntl();
  const day = today();
  const kioskToken = getKioskToken();
  const { logout } = useUser();
  const { data, refetch } = usePortalHomeQuery({
    variables: { today: day, kioskToken },
    fetchPolicy: "cache-and-network",
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  // "Cheguei mais cedo": an earlier time for gerência to approve.
  const [claim, setClaim] = useState({ open: false, time: "", reason: "" });
  const [punch, punchOpts] = usePortalPunchCreateMutation();
  const state = data?.myPunchState;
  const needsPin = (kind: PunchKindEnum) =>
    (kind === PunchKindEnum.IN && !!state?.entryPinRequired) ||
    (kind === PunchKindEnum.BREAK_END && !!state?.lunchPinRequired);
  const pinNeeded = (state?.nextKinds ?? []).some(needsPin);

  const handlePunch = async (kind: PunchKindEnum) => {
    setNotice(null);

    const result = await punch({
      variables: {
        kind,
        deviceId: getDeviceId(),
        kioskToken,
        pin: needsPin(kind) ? pin.trim() : undefined,
        ...(kind === PunchKindEnum.IN && claim.open && claim.time
          ? { claimedAt: lisbonToIso(day, claim.time), claimReason: claim.reason.trim() }
          : {}),
      },
    });
    const saved = result.data?.punchCreate?.punch;

    if (saved) {
      setPin("");
      setClaim({ open: false, time: "", reason: "" });
      setNotice(intl.formatMessage(m.punchSaved, { time: formatTime(saved.effectiveAt) }));
      refetch();

      if (kioskToken) {
        // Shared device: sign the person out so the next one can punch.
        window.setTimeout(() => logout?.(), 5000);
      }
    }
  };

  const status = state?.lastKind ? STATUS[state.lastKind] : m.statusOut;
  const busy = punchOpts.loading;
  const reportMissingToday =
    !!data?.myWorkSettings?.dailyReportRequired && (data?.myMissingReports.includes(day) ?? false);
  const reportBeforeLeaving =
    !!data?.myWorkSettings?.dailyReportRequired &&
    !!state?.nextKinds.includes(PunchKindEnum.OUT) &&
    reportMissingToday;
  const pendingWeeks = data?.myPendingWeeks.length ?? 0;
  const pendingDays = data?.myPendingDays ?? [];
  const olderDays = pendingDays.filter(d => d !== day);
  const todayToSign =
    data?.myDay && pendingDays.includes(day) && state?.lastKind === PunchKindEnum.OUT;
  // Previous days without rubrica: signed here, before a new day can start.
  const previousToSign = olderDays.filter(d => d < day).sort();
  const previous = usePortalPeriodQuery({
    variables: {
      start: previousToSign[0] ?? day,
      end: previousToSign[previousToSign.length - 1] ?? day,
    },
    skip: previousToSign.length === 0,
    fetchPolicy: "cache-and-network",
  });

  return (
    <Page>
      <Text size={8} fontWeight="bold">
        {intl.formatMessage(m.hello, {
          name: data?.myEmployeeProfile?.fullName.split(" ")[0] ?? "",
        })}
      </Text>
      {(pendingWeeks > 0 || olderDays.length > 0) && (
        <Box
          display="flex"
          gap={3}
          alignItems="center"
          padding={3}
          borderRadius={4}
          backgroundColor="warning1"
        >
          <Text size={3}>
            {olderDays.length > 0
              ? intl.formatMessage(m.pendingDays, { count: olderDays.length })
              : intl.formatMessage(m.pendingWeeks, { count: pendingWeeks })}
          </Text>
          <Link to={portalPaths.records}>
            <Button size="small" variant="secondary" as="span">
              {intl.formatMessage(m.goSign)}
            </Button>
          </Link>
        </Box>
      )}
      {previousToSign.length > 0 && (
        <>
          <Text size={3} color="warning1">
            {intl.formatMessage(m.signPreviousFirst)}
          </Text>
          {(previous.data?.myPeriod?.days ?? [])
            .filter(summary => previousToSign.includes(summary.date))
            .map(summary => (
              <DailySign
                key={summary.date}
                day={summary}
                onDone={() => {
                  refetch();
                  previous.refetch();
                }}
              />
            ))}
        </>
      )}
      <Section title={intl.formatMessage(status)}>
        {pinNeeded && (
          <Box __maxWidth="220px">
            <Input
              size="large"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              label={intl.formatMessage(
                state?.lunchPinRequired ? m.lunchPinLabel : m.entryPinLabel,
              )}
              value={pin}
              onChange={event => setPin(event.target.value.replace(/\D/g, ""))}
            />
          </Box>
        )}
        {state?.nextKinds.includes(PunchKindEnum.IN) &&
          previousToSign.length === 0 &&
          (claim.open ? (
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
              <Input
                size="small"
                type="time"
                label={intl.formatMessage(m.claimTime)}
                value={claim.time}
                onChange={event => setClaim({ ...claim, time: event.target.value })}
              />
              <Input
                size="small"
                label={intl.formatMessage(m.reason)}
                value={claim.reason}
                maxLength={500}
                onChange={event => setClaim({ ...claim, reason: event.target.value })}
              />
              <Text size={1} color="default2">
                {intl.formatMessage(m.claimHint)}
              </Text>
            </Box>
          ) : (
            <Box>
              <Button
                size="small"
                variant="tertiary"
                onClick={() => setClaim({ ...claim, open: true })}
              >
                {intl.formatMessage(m.claimOpen)}
              </Button>
            </Box>
          ))}
        <Box display="grid" gap={3} __gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))">
          {(state?.nextKinds ?? []).map(kind => (
            <Button
              key={kind}
              size="large"
              variant={
                kind === PunchKindEnum.IN || kind === PunchKindEnum.OUT ? "primary" : "secondary"
              }
              disabled={
                busy ||
                (kind === PunchKindEnum.IN && previousToSign.length > 0) ||
                (needsPin(kind) && pin.trim().length !== 6) ||
                (kind === PunchKindEnum.IN && claim.open && (!claim.time || !claim.reason.trim()))
              }
              onClick={() => handlePunch(kind)}
              data-test-id={`punch-${kind.toLowerCase()}`}
              __minHeight="64px"
            >
              {intl.formatMessage(KIND_MESSAGES[kind])}
            </Button>
          ))}
        </Box>
        {state && !state.onSiteNetwork && (
          <Text size={3} color="warning1">
            {intl.formatMessage(m.offNetwork)}
          </Text>
        )}
        {kioskToken && (
          <Text size={2} color="default2">
            {intl.formatMessage(m.kioskMode)}
          </Text>
        )}
        {reportBeforeLeaving && (
          <Box display="flex" gap={2} alignItems="center">
            <Text size={3} color="warning1">
              {intl.formatMessage(m.reportBeforeLeaving)}
            </Text>
            <Link to={portalPaths.tasks}>
              <Button size="small" variant="secondary" as="span">
                {intl.formatMessage(m.writeReport)}
              </Button>
            </Link>
          </Box>
        )}
        {notice && (
          <Text size={3} color="success1">
            {notice}
          </Text>
        )}
        <ErrorText errors={punchOpts.data?.punchCreate?.errors} />
        <Text size={1} color="default2">
          {intl.formatMessage(m.networkNotice)}
        </Text>
      </Section>
      {todayToSign && data?.myDay && <DailySign day={data.myDay} onDone={refetch} />}
      {data?.myDay && (
        <Section
          title={`${intl.formatMessage(m.workedToday)}: ${formatMinutes(data.myDay.workedMinutes)}`}
        >
          <DayCard day={data.myDay} worker onChanged={refetch} />
        </Section>
      )}
    </Page>
  );
};
